<template>
  <v-card flat class="rmr-table-shell">
    <div class="rmr-table-shell__header">
      <div class="above-table">
        <rmr-text-field
          v-model="search"
          label="Recherche..."
          append-icon="mdi-magnify"
        />
        <rmr-select
          v-model="actionFilter"
          :items="actionOptions"
          label="Filtrer par action"
          clearable
        />
      </div>
    </div>

    <v-card-text class="rmr-table-shell__body">
      <v-data-table
        locale="fr-FR"
        :page="options.page"
        :pageCount="pageCount"
        :headers="headers"
        :items="filteredItems"
        :sort-by="'updated'"
        :sort-desc="true"
        :options.sync="options"
        :loading="loading"
        class="rmr-data-table"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100],
        }"
        :items-per-page="options.itemsPerPage"
      >
        <template v-slot:loading>
          <div class="rmr-data-table__state">
            <v-progress-circular indeterminate color="#6366F1" size="28" class="mb-2" />
            <div class="rmr-data-table__state-title">Chargement des permissions</div>
          </div>
        </template>

        <template v-slot:no-data>
          <div class="rmr-data-table__state">
            <div class="rmr-data-table__state-title">
              {{ hasActiveFilters ? "Aucune permission trouvée" : "Aucune permission disponible" }}
            </div>
            <div class="rmr-data-table__state-text">
              {{
                hasActiveFilters
                  ? "Aucune permission ne correspond à la recherche en cours."
                  : "Créez une première permission pour alimenter les rôles."
              }}
            </div>
          </div>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex" style="gap: 8px">
            <rmr-icon-button
              :icon="icons.mdiPencil"
              variant="edit"
              tooltip="Modifier la permission"
              @click="$emit('edit', item)"
            />
            <rmr-icon-button
              :icon="icons.mdiTrashCan"
              variant="delete"
              tooltip="Supprimer la permission"
              @click="
                dialogModel.dialog = true;
                dialogModel.id = item.id;
                dialogModel.entryName = item.name;
              "
            />
          </div>
        </template>
      </v-data-table>
    </v-card-text>
    <rmr-confirm-dialog
      v-model="dialogModel.dialog"
      title="Confirmation Suppression"
      :message="`La permission '${dialogModel.entryName}' et toutes ses references associees seront supprimees.`"
      confirm-label="Supprimer"
      cancel-label="Annuler"
      danger
      @confirm="deleteEntry"
    />
  </v-card>
</template>
<style scoped></style>
<script>
import axios from "@axios";
import store from "@/store";
import RmrConfirmDialog from "@/components/ui/actions/RmrConfirmDialog.vue";
import RmrIconButton from "@/components/ui/actions/RmrIconButton.vue";
import RmrSelect from "@/components/ui/forms/RmrSelect.vue";
import RmrTextField from "@/components/ui/forms/RmrTextField.vue";
import { mdiPencil, mdiTrashCan } from "@mdi/js";
import debounce from "lodash/debounce";

export default {
  name: "PermissionsTable",
  components: { RmrConfirmDialog, RmrIconButton, RmrSelect, RmrTextField },
  created() {
    this.debouncedUpdateData();
  },
  data() {
    return {
      search: "",
      actionFilter: null,
      items: [],
      loading: false,
      total: 0,
      options: {
        page: 1,
        itemsPerPage: 10,
        total: 0,
      },
      dialogModel: {
        dialog: false,
        id: -1,
        entryName: "",
      },
      headers: [
        {
          text: "ID",
          value: "id",
        },
        {
          text: "Nom",
          align: "start",
          value: "name",
        },
        {
          text: "Subject",
          value: "subject",
        },
        {
          text: "Action",
          value: "action",
        },
        {
          text: "Description",
          value: "description",
        },
        {
          value: "actions",
        },
      ],
      icons: {
        mdiPencil,
        mdiTrashCan,
      },
      actionOptions: ["read", "manage", "no_action"],
    };
  },
  watch: {
    options: {
      handler() {
        this.debouncedUpdateData();
      },
    },
    search() {
      this.options.page = 1;
    },
    actionFilter() {
      this.options.page = 1;
    },
  },
  computed: {
    pageCount() {
      return Math.ceil(this.filteredItems.length / this.options.itemsPerPage) || 1;
    },
    hasActiveFilters() {
      return Boolean(this.search || this.actionFilter);
    },
    filteredItems() {
      const searchText = (this.search || "").toLowerCase().trim();

      return this.items.filter((item) => {
        const matchesSearch =
          !searchText ||
          [item.name, item.subject, item.action, item.description]
            .filter(Boolean)
            .some((value) => value.toLowerCase().includes(searchText));

        if (!matchesSearch) {
          return false;
        }

        return !this.actionFilter || item.action === this.actionFilter;
      });
    },
  },
  methods: {
    debouncedUpdateData: debounce(async function () {
      await this.updateData();
    }, 500),
    async updateData() {
      this.loading = true;
      const { data } = await axios.get("/permissions/");
      this.loading = false;
      this.items = data.items;
      this.total = data.total;
    },
    truncate(input) {
      return input.length > 5 ? `${input.substring(0, 30)}...` : input;
    },
    async deleteEntry() {
      const { status } = await axios.delete(`/permissions/${this.dialogModel.id}`);
      if (status === 204) {
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: `La permission '${this.dialogModel.entryName}' et toutes ses references ont ete supprimees.`,
          color: "success",
          timeout: 4000,
        });
        this.dialogModel.dialog = false;
        this.debouncedUpdateData();
      }
    },
    async reload() {
      await this.updateData();
    },
  },
};
</script>
<style lang="scss" scoped>
.above-table {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

@media (max-width: 960px) {
  .above-table {
    grid-template-columns: 1fr;
  }
}
</style>
